import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  EnvelopeIcon,
  PaperClipIcon,
  ExclamationCircleIcon,
  XCircleIcon,
  PhoneIcon,
  PlayIcon,
  PauseIcon,
  StopIcon,
  CogIcon,
  UserIcon,
  ArrowPathIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';
import { useUnassignedEmails, useDiscardEmail, useTriageStats } from '../../tracker/hooks/useTrackerEmails';
import type { TrackerEmail } from '../../tracker/types/tracker.types';
import { ProcessEmailModal } from './ProcessEmailModal';
import { EmailContentModal } from './EmailContentModal';
import { EmailReallocationModal } from '../../tracker/components/EmailReallocationModal';
import { ExpertisesList } from '../../search/components/ExpertisesList';
import { ControlsContainer } from '@/ui/components/molecules/ControlsContainer';
import { Loading } from '@/ui/components/atoms/Loading';
import { isVoicemail, getVoicemailAttachment } from '../utils/voicemail';
import { VoicemailTranscription } from './VoicemailTranscription';
import { emailsAPI } from '../../tracker/api/emails.api';
import type { Customer } from '../../search/types/search.types';
import type { VoicemailMatchedClient } from '../../tracker/types/tracker.types';
import { useTriageHistory } from '../../agent-lea/hooks/useTriageHistory';
import { BannedSendersPanel } from './BannedSendersPanel';
import type { TriageDecision } from '../../agent-lea/types/agent.types';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export const UnassignedEmails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selectedEmail, setSelectedEmail] = useState<TrackerEmail | null>(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [viewEmailModal, setViewEmailModal] = useState<TrackerEmail | null>(null);
  const [reallocateEmail, setReallocateEmail] = useState<TrackerEmail | null>(null);
  const [playingAudioId, setPlayingAudioId] = useState<string | null>(null);
  const [activeAudioPlayerId, setActiveAudioPlayerId] = useState<string | null>(null);
  const [audioElements, setAudioElements] = useState<Map<string, HTMLAudioElement>>(new Map());
  const [audioCurrentTime, setAudioCurrentTime] = useState<Map<string, number>>(new Map());
  const [audioDuration, setAudioDuration] = useState<Map<string, number>>(new Map());
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [transcriptions, setTranscriptions] = useState<Map<string, string>>(new Map());
  const transcribingRef = useRef<Set<string>>(new Set());

  // Filter state
  const [filters, setFilters] = useState({
    searchQuery: '',
    sortBy: 'date',
    sortOrder: 'desc' as 'asc' | 'desc',
    activeFilter: 'new_affaire'
  });

  const updateFilter = (key: string, value: string | 'asc' | 'desc') => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Triage stats for badge counts
  const { data: triageStatsData, refetch: refetchTriageStats } = useTriageStats();
  const triageStats = triageStatsData?.data;

  // Determine query params based on active filter
  const getQueryParams = () => {
    switch (filters.activeFilter) {
      case 'pending':
        return { triage_flag: 'pending', limit: 100 };
      case 'auto_assigned':
        return { triage_flag: 'auto_assigned', limit: 100 };
      case 'new_affaire':
        return { triage_flag: 'new_affaire', limit: 100 };
      case 'interesting':
        return { triage_flag: 'interesting', limit: 100 };
      case 'all_received':
        return { triage_flag: 'all_received', limit: 100 };
      case 'discarded':
        return { discardedOnly: true, limit: 100 };
      case 'voicemails':
        return { triage_flag: 'voicemails', limit: 100 };
      default: // 'all'
        return { includeDiscarded: false, limit: 100 };
    }
  };

  const queryParams = getQueryParams();
  const { data: emailsData, isLoading, refetch: refetchEmails } = useUnassignedEmails(queryParams);

  const emails = emailsData?.data || [];

  const refetch = () => {
    refetchEmails();
    refetchTriageStats();
  };

  // Auto-transcribe voicemails that don't have a transcription yet
  useEffect(() => {
    const untranscribed = emails.filter(
      (e) => isVoicemail(e) && !e.voicemail_transcription && !transcriptions.has(e.id) && !transcribingRef.current.has(e.id)
    );
    for (const email of untranscribed) {
      transcribingRef.current.add(email.id);
      emailsAPI.transcribeVoicemail(email.id).then((res: { success: boolean; transcription: string }) => {
        if (res.success && res.transcription) {
          setTranscriptions((prev) => new Map(prev).set(email.id, res.transcription));
        }
      }).catch(() => {
        // Silently fail — transcription is best-effort
      }).finally(() => {
        transcribingRef.current.delete(email.id);
      });
    }
  }, [emails]);

  // Filtered and sorted emails (client-side search + voicemail filter + sort)
  const filteredEmails = useMemo(() => {
    let result = [...emails];

    // For 'all' filter, apply voicemail sub-filter only when needed
    if (filters.activeFilter === 'voicemails') {
      result = result.filter(e => isVoicemail(e));
    }

    // Apply search
    if (filters.searchQuery) {
      const query = filters.searchQuery.toLowerCase();
      result = result.filter(e =>
        (e.subject?.toLowerCase().includes(query)) ||
        (e.sender_name?.toLowerCase().includes(query)) ||
        (e.sender_address?.toLowerCase().includes(query))
      );
    }

    // Apply sort
    result.sort((a, b) => {
      let comparison = 0;
      switch (filters.sortBy) {
        case 'date':
          comparison = new Date(b.received_time || b.email_time).getTime() -
                       new Date(a.received_time || a.email_time).getTime();
          break;
        case 'sender':
          comparison = (a.sender_name || a.sender_address || '').localeCompare(
            b.sender_name || b.sender_address || ''
          );
          break;
        case 'subject':
          comparison = (a.subject || '').localeCompare(b.subject || '');
          break;
      }
      return filters.sortOrder === 'asc' ? -comparison : comparison;
    });

    return result;
  }, [emails, filters]);

  // Triage history for "Actions Lea" tab
  const isLeaActionsTab = filters.activeFilter === 'lea_actions';
  const { triageRuns, isLoading: isTriageLoading } = useTriageHistory(isLeaActionsTab);

  // Flatten all triage decisions into a single chronological list, deduplicated by email_id
  const flattenedActions = useMemo(() => {
    if (!isLeaActionsTab || triageRuns.length === 0) return [];
    const seen = new Set<string>();
    const actions: Array<TriageDecision & { run_at: string }> = [];
    // Runs are already sorted newest first from the API
    for (const run of triageRuns) {
      if (!run.decisions) continue;
      for (const d of run.decisions) {
        if (seen.has(d.email_id)) continue;
        seen.add(d.email_id);
        actions.push({ ...d, run_at: run.run_at });
      }
    }
    return actions;
  }, [isLeaActionsTab, triageRuns]);

  const discardMutation = useDiscardEmail();

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'dd/MM/yyyy HH:mm', { locale: fr });
    } catch {
      return dateString;
    }
  };

  const handleDiscard = async (email: TrackerEmail) => {
    try {
      await discardMutation.mutateAsync({
        emailId: email.id
      });
      refetch();
    } catch (error) {
      console.error('Error archiving email:', error);
      alert('Erreur lors de l\'archivage de l\'email');
    }
  };

  const handlePlayVoicemail = async (email: TrackerEmail) => {
    const emailId = email.id;
    const attachment = getVoicemailAttachment(email);
    if (!attachment) return;

    if (activeAudioPlayerId && activeAudioPlayerId !== emailId) {
      const currentAudio = audioElements.get(activeAudioPlayerId);
      if (currentAudio) {
        currentAudio.pause();
        currentAudio.currentTime = 0;
      }
    }

    let audio = audioElements.get(emailId);

    if (!audio) {
      try {
        const token = localStorage.getItem('authToken');
        const baseURL = import.meta.env.VITE_API_URL || 'https://myapp.chatelainxpert.be';
        const attachmentIndex = email.attachments?.findIndex(a => a.id === attachment.id) || 0;
        const response = await fetch(
          `${baseURL}/api/admin/emails/${email.id}/attachments/${attachmentIndex}/download?inline=true`,
          {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }
        );

        if (response.ok) {
          const blob = await response.blob();
          const url = URL.createObjectURL(blob);
          audio = new Audio(url);

          audio.addEventListener('ended', () => {
            setPlayingAudioId(null);
          });

          audio.addEventListener('loadedmetadata', () => {
            const newDuration = new Map(audioDuration);
            newDuration.set(emailId, audio!.duration);
            setAudioDuration(newDuration);
          });

          audio.addEventListener('timeupdate', () => {
            const newCurrentTime = new Map(audioCurrentTime);
            newCurrentTime.set(emailId, audio!.currentTime);
            setAudioCurrentTime(newCurrentTime);
          });

          const newAudioElements = new Map(audioElements);
          newAudioElements.set(emailId, audio);
          setAudioElements(newAudioElements);
        }
      } catch (error) {
        console.error('Error loading audio:', error);
        return;
      }
    }

    audio?.play();
    setPlayingAudioId(emailId);
    setActiveAudioPlayerId(emailId);
  };

  const handlePauseVoicemail = (emailId: string) => {
    const audio = audioElements.get(emailId);
    if (audio) {
      audio.pause();
      setPlayingAudioId(null);
    }
  };

  const handleStopVoicemail = (emailId: string) => {
    const audio = audioElements.get(emailId);
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
      setPlayingAudioId(null);
      setActiveAudioPlayerId(null);
    }
  };

  const handleResumeVoicemail = (emailId: string) => {
    const audio = audioElements.get(emailId);
    if (audio) {
      audio.play();
      setPlayingAudioId(emailId);
    }
  };

  const handleSeekVoicemail = (emailId: string, value: number) => {
    const audio = audioElements.get(emailId);
    if (audio) {
      audio.currentTime = value;
      const newCurrentTime = new Map(audioCurrentTime);
      newCurrentTime.set(emailId, value);
      setAudioCurrentTime(newCurrentTime);
    }
  };

  const formatTime = (seconds: number): string => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup audio elements on unmount
  useEffect(() => {
    return () => {
      audioElements.forEach((audio) => {
        audio.pause();
        audio.src = '';
      });
    };
  }, []);

  // Helper function to convert VoicemailMatchedClient to Customer for ExpertisesList
  const convertToCustomer = (client: VoicemailMatchedClient): Customer => ({
    id: client.id || '',
    fmClientId: client.fmClientId,
    firstName: client.firstName,
    lastName: client.lastName,
    email: client.email || undefined,
    phone: client.phone || undefined,
    phoneWork: client.phoneWork || undefined,
    phoneMobile: client.phoneMobile || undefined,
    typeContact: client.typeContact || undefined,
    address: client.address ? {
      street: client.address.street || undefined,
      city: client.address.city || undefined,
      postalCode: client.address.postalCode || undefined
    } : undefined,
    hasAccount: client.hasAccount,
    userEmail: client.userEmail || undefined,
    role: client.role || undefined,
    isActive: client.isActive || undefined
  });

  // Show ExpertisesList if customer is selected
  if (selectedCustomer) {
    return (
      <ExpertisesList
        customer={selectedCustomer}
        onBack={() => setSelectedCustomer(null)}
      />
    );
  }

  // Build filter options with badge counts
  const filterOptions = [
    { key: 'new_affaire', label: 'Nouvelles demandes', notificationCount: triageStats?.new_affaire && triageStats.new_affaire > 0 ? triageStats.new_affaire : undefined },
    { key: 'interesting', label: 'A traiter', notificationCount: triageStats?.interesting && triageStats.interesting > 0 ? triageStats.interesting : undefined },
    { key: 'all_received', label: 'Tous les emails' },
    { key: 'discarded', label: 'Archives' },
    { key: 'lea_actions', label: 'Actions Lea' },
    { key: 'auto_assigned', label: 'Tries par Lea' },
    { key: 'pending', label: 'A trier' },
    { key: 'banned_senders', label: 'Bloqués' }
  ];

  // Determine if we're in a read-only triage view
  const isTriageView = ['auto_assigned', 'new_affaire', 'interesting'].includes(filters.activeFilter);
  const viewingDiscarded = filters.activeFilter === 'discarded';
  const showTriageDetails = isTriageView || filters.activeFilter === 'all_received' || viewingDiscarded;

  return (
    <div className="admin-mes-dossiers-page">
      {/* Header */}
      <div className="admin-mes-dossiers-page__header">
        <div className="admin-mes-dossiers-page__header-content">
          <h1 className="admin-mes-dossiers-page__title">Emails</h1>
          <p className="admin-mes-dossiers-page__subtitle">
            Gerer les emails non assignes et les associer aux dossiers appropries
          </p>
        </div>
        <div className="admin-mes-dossiers-page__actions">
          {triageStats && (
            <>
              <span className="admin-mes-dossiers-stat">
                A trier: <strong>{triageStats.pending}</strong>
              </span>
              <span className="admin-mes-dossiers-stat">
                Tries: <strong>{triageStats.auto_assigned}</strong>
              </span>
              <span className="admin-mes-dossiers-stat">
                Archives: <strong>{triageStats.discarded}</strong>
              </span>
            </>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="admin-mes-dossiers-page__content">
        {/* Controls */}
        <ControlsContainer
          searchQuery={filters.searchQuery}
          onSearchChange={(value) => updateFilter('searchQuery', value)}
          sortBy={filters.sortBy}
          onSortChange={(value) => updateFilter('sortBy', value)}
          sortOrder={filters.sortOrder}
          onSortOrderChange={(value) => updateFilter('sortOrder', value)}
          sortOptions={[
            { value: 'date', label: 'Date' },
            { value: 'sender', label: 'Expediteur' },
            { value: 'subject', label: 'Sujet' }
          ]}
          activeFilter={filters.activeFilter}
          onFilterChange={(value) => updateFilter('activeFilter', value)}
          filterOptions={filterOptions}
          showFilters={true}
          resultsCount={filteredEmails.length}
          searchPlaceholder="Rechercher par sujet, expediteur..."
          afterFilters={
            <button
              className="btn-actualiser"
              onClick={() => refetch()}
              disabled={isLoading}
            >
              <ArrowPathIcon className={isLoading ? 'spinning' : ''} />
              Actualiser
            </button>
          }
        />

        {/* "Wait for Lea" banner on pending tab */}
        {filters.activeFilter === 'pending' && filteredEmails.length > 0 && (
          <div className="triage-pending-banner">
            <span className="triage-pending-banner__icon">&#9203;</span>
            <span>Ces emails sont en attente du tri automatique par Lea. Le tri se declenche quand 3+ emails sont en attente.</span>
          </div>
        )}

        {/* ===== Actions Lea tab: flat list of all triage decisions ===== */}
        {isLeaActionsTab && (
          <>
            {isTriageLoading && (
              <Loading size="large" text="Chargement des actions..." />
            )}
            {!isTriageLoading && flattenedActions.length === 0 && (
              <div className="emails-empty-state">
                <div className="emails-empty-state__icon"><EnvelopeIcon /></div>
                <h3 className="emails-empty-state__title">Aucune action</h3>
                <p className="emails-empty-state__subtitle">Lea n'a pas encore effectue de tri d'emails</p>
              </div>
            )}
            {!isTriageLoading && flattenedActions.length > 0 && (
              <div className="lea-actions-log">
                {flattenedActions.map((action, idx) => {
                  const actionConfig: Record<string, { icon: string; label: string; cls: string }> = {
                    assign: { icon: '\u2705', label: 'Assigne', cls: 'lea-action--assigned' },
                    discard: { icon: '\ud83d\uddd1\ufe0f', label: 'Archive', cls: 'lea-action--discarded' },
                    flag_new_affaire: { icon: '\ud83c\udd95', label: 'Nouvelle demande', cls: 'lea-action--new' },
                    flag_interesting: { icon: '\u2b50', label: 'A traiter', cls: 'lea-action--interesting' },
                    skip: { icon: '\u23ed\ufe0f', label: 'Ignore', cls: 'lea-action--skipped' },
                  };
                  const cfg = actionConfig[action.action] || actionConfig.skip;
                  const dateStr = (() => {
                    try {
                      return format(new Date(action.run_at), 'dd/MM HH:mm', { locale: fr });
                    } catch { return ''; }
                  })();

                  return (
                    <div key={`${action.email_id}-${idx}`} className={`lea-action-row ${cfg.cls}`}>
                      <span className="lea-action-row__icon">{cfg.icon}</span>
                      <span className="lea-action-row__date">{dateStr}</span>
                      <span className="lea-action-row__subject">
                        {action.subject_snippet || action.email_id.slice(0, 12) + '...'}
                      </span>
                      {action.action === 'assign' && action.affaire_id && (
                        <span
                          className="lea-action-row__affaire lea-action-row__affaire--clickable"
                          onClick={() => {
                            const basePath = location.pathname.includes('/bureau/') ? '/bureau' : '/admin';
                            navigate(`${basePath}/tracker/${action.affaire_id}`);
                          }}
                        >
                          Dossier {action.affaire_id}
                        </span>
                      )}
                      <span className="lea-action-row__label">{cfg.label}</span>
                      {action.reason && (
                        <span className="lea-action-row__reason">{action.reason}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* ===== Banned Senders tab ===== */}
        {filters.activeFilter === 'banned_senders' && (
          <BannedSendersPanel />
        )}

        {/* Loading State */}
        {!isLeaActionsTab && filters.activeFilter !== 'banned_senders' && isLoading && (
          <Loading size="large" text="Chargement des emails..." />
        )}

        {/* Empty State */}
        {!isLeaActionsTab && filters.activeFilter !== 'banned_senders' && !isLoading && filteredEmails.length === 0 && (
          <div className="emails-empty-state">
            <div className="emails-empty-state__icon">
              <EnvelopeIcon />
            </div>
            <h3 className="emails-empty-state__title">
              {filters.searchQuery
                ? 'Aucun resultat'
                : 'Aucun email'}
            </h3>
            <p className="emails-empty-state__subtitle">
              {filters.searchQuery
                ? 'Aucun email ne correspond aux criteres de recherche'
                : 'Aucun email dans cette categorie'}
            </p>
          </div>
        )}

        {/* Email List */}
        {!isLeaActionsTab && filters.activeFilter !== 'banned_senders' && !isLoading && filteredEmails.length > 0 && (
          <div className="email-list-redesigned">
            {filteredEmails.map((email) => {
            const hasContent = email.full_content || email.full_content_html || email.body_preview || (email.has_attachments && email.attachments.length > 0);
            const emailIsVoicemail = isVoicemail(email);
            const hasActivePlayer = activeAudioPlayerId === email.id;
            const isPlaying = playingAudioId === email.id;
            const hasMatchedClients = email.matched_clients && email.matched_clients.length > 0;

            return (
              <div
                key={email.id}
                className={`email-row-card ${emailIsVoicemail ? 'email-row-card--voicemail' : ''}`}
              >
                <div
                  className="email-row-card__main"
                  onClick={() => !emailIsVoicemail && hasContent && setViewEmailModal(email)}
                  style={{ cursor: !emailIsVoicemail && hasContent ? 'pointer' : 'default' }}
                >
                  {/* Icon */}
                  <div className={`email-row-card__icon ${emailIsVoicemail ? 'email-row-card__icon--voicemail' : 'email-row-card__icon--email'}`}>
                    {emailIsVoicemail ? <PhoneIcon /> : <EnvelopeIcon />}
                  </div>

                  {/* Content */}
                  <div className="email-row-card__content">
                    {/* First line: Subject + triage badge */}
                    <div className="email-row-card__subject">
                      <span>{email.subject || '(Sans objet)'}</span>
                      {/* Triage badges */}
                      {(email as any).triage_flag === 'auto_assigned' && (email as any).affaire_id && (
                        <span className="email-row-card__triage-badge email-row-card__triage-badge--assigned">
                          &#8594; Dossier {(email as any).affaire_id}
                        </span>
                      )}
                      {(email as any).triage_flag === 'new_affaire' && (
                        <span className="email-row-card__triage-badge email-row-card__triage-badge--new-affaire">
                          Nouvelle demande
                        </span>
                      )}
                      {(email as any).triage_flag === 'interesting' && (
                        <span className="email-row-card__triage-badge email-row-card__triage-badge--interesting">
                          A traiter
                        </span>
                      )}
                    </div>

                    {/* Second line: Sender + Date */}
                    <div className="email-row-card__meta">
                      <span className="email-row-card__sender">
                        {email.sender_name || email.sender_address}
                      </span>
                      <span className="email-row-card__separator">&#8226;</span>
                      <span className="email-row-card__date">
                        {formatDate(email.received_time ?? email.email_time)}
                      </span>
                      {email.importance === 'high' && (
                        <span className="email-row-card__badge email-row-card__badge--important">
                          <ExclamationCircleIcon />
                        </span>
                      )}
                      {email.has_attachments && (
                        <span className="email-row-card__badge email-row-card__badge--attachment">
                          <PaperClipIcon />
                          <span>{email.attachments.length}</span>
                        </span>
                      )}
                      {emailIsVoicemail && (
                        <span className="email-row-card__badge email-row-card__badge--duration">
                          <PlayIcon />
                          <span>{audioDuration.get(email.id) ? formatTime(audioDuration.get(email.id)!) : '0:00'}</span>
                        </span>
                      )}
                    </div>

                    {/* Triage reason (for triaged emails) */}
                    {(email as any).triage_reason && showTriageDetails && (
                      <div className="email-row-card__triage-reason">
                        {(email as any).triage_reason}
                      </div>
                    )}

                    {/* Voicemail transcription */}
                    {emailIsVoicemail && (
                      <VoicemailTranscription
                        transcription={email.voicemail_transcription || transcriptions.get(email.id)}
                      />
                    )}

                    {/* Third line: Matched clients (if any) */}
                    {hasMatchedClients && (
                      <div className="email-row-card__matched">
                        {email.matched_clients!.slice(0, 2).map((client) => (
                          <button
                            key={client.fmClientId}
                            className="email-row-card__matched-tag"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCustomer(convertToCustomer(client));
                            }}
                          >
                            <UserIcon className="email-row-card__matched-tag-icon" />
                            <span className="email-row-card__matched-tag-name">
                              {client.firstName} {client.lastName}
                            </span>
                            <ArrowTopRightOnSquareIcon className="email-row-card__matched-tag-arrow" />
                          </button>
                        ))}
                        {email.matched_clients!.length > 2 && (
                          <span className="email-row-card__matched-more-tag">
                            +{email.matched_clients!.length - 2}
                          </span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Circular Action Buttons */}
                  <div className="email-row-card__actions">
                    {emailIsVoicemail && (
                      <div className="email-action-btn email-action-btn--play" onClick={(e) => {
                        e.stopPropagation();
                        if (hasActivePlayer) {
                          handleStopVoicemail(email.id);
                        } else {
                          handlePlayVoicemail(email);
                        }
                      }}>
                        <div className="email-action-btn__circle">
                          {hasActivePlayer ? <StopIcon /> : <PlayIcon />}
                        </div>
                        <span className="email-action-btn__label">{hasActivePlayer ? 'STOP' : 'ECOUTER'}</span>
                      </div>
                    )}

                    <div className="email-action-btn email-action-btn--primary" onClick={(e) => {
                      e.stopPropagation();
                      setSelectedEmail(email);
                      setShowProcessModal(true);
                    }}>
                      <div className="email-action-btn__circle">
                        <CogIcon />
                      </div>
                      <span className="email-action-btn__label">TRAITER</span>
                    </div>

                    {!viewingDiscarded && !isTriageView && (
                      <div className="email-action-btn email-action-btn--danger" onClick={(e) => {
                        e.stopPropagation();
                        handleDiscard(email);
                      }}>
                        <div className="email-action-btn__circle">
                          <XCircleIcon />
                        </div>
                        <span className="email-action-btn__label">ARCHIVER</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Audio Player for Voicemail */}
                {emailIsVoicemail && hasActivePlayer && (
                  <div className="voicemail-audio-player" onClick={(e) => e.stopPropagation()}>
                    <div className="audio-player-controls">
                      {isPlaying ? (
                        <button
                          className="audio-control-btn"
                          onClick={() => handlePauseVoicemail(email.id)}
                          title="Pause"
                        >
                          <PauseIcon />
                        </button>
                      ) : (
                        <button
                          className="audio-control-btn"
                          onClick={() => handleResumeVoicemail(email.id)}
                          title="Reprendre"
                        >
                          <PlayIcon />
                        </button>
                      )}
                      <button
                        className="audio-control-btn"
                        onClick={() => handleStopVoicemail(email.id)}
                        title="Stop"
                      >
                        <StopIcon />
                      </button>
                    </div>
                    <div className="audio-player-progress">
                      <span className="audio-time">{formatTime(audioCurrentTime.get(email.id) || 0)}</span>
                      <input
                        type="range"
                        className="audio-slider"
                        min="0"
                        max={audioDuration.get(email.id) || 100}
                        value={audioCurrentTime.get(email.id) || 0}
                        onChange={(e) => handleSeekVoicemail(email.id, parseFloat(e.target.value))}
                        step="0.1"
                      />
                      <span className="audio-time">{formatTime(audioDuration.get(email.id) || 0)}</span>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}
      </div>

      {/* Process Email Modal */}
      {showProcessModal && selectedEmail && (
        <ProcessEmailModal
          email={selectedEmail}
          onClose={() => {
            setShowProcessModal(false);
            setSelectedEmail(null);
          }}
          onSuccess={() => {
            refetch();
          }}
        />
      )}

      {/* Email Content Modal */}
      {viewEmailModal && (
        <EmailContentModal
          email={viewEmailModal}
          onClose={() => setViewEmailModal(null)}
          currentAffaireId={
            (viewEmailModal as any).affaire_id
              ? Number((viewEmailModal as any).affaire_id)
              : viewEmailModal.case_number
                ? Number(viewEmailModal.case_number)
                : undefined
          }
          onReallocate={(email) => {
            setViewEmailModal(null);
            setReallocateEmail(email);
          }}
        />
      )}

      {/* Email Reallocation Modal */}
      {reallocateEmail && (
        <EmailReallocationModal
          email={reallocateEmail}
          currentAffaireId={
            (reallocateEmail as any).affaire_id
              ? Number((reallocateEmail as any).affaire_id)
              : Number(reallocateEmail.case_number)
          }
          onClose={() => setReallocateEmail(null)}
          onSuccess={() => {
            setReallocateEmail(null);
            refetch();
          }}
        />
      )}
    </div>
  );
};
